// Onglet « Garanties » côté bailleur : garants et acte de cautionnement (F-032), Visale (F-033), garanties loyers impayés (F-034).
import { useState } from "react";
import { patch, post, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money, today } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, ButtonLink, Card, Checkbox, Empty, ErrorBox, FileDrop, Fil, Input, Loading, Modal, MoneyInput, Notice, Select, StatusChip, fieldError, type CheckStatus } from "@core/ui";
import { CheckLine, OfficialLink, docUrl, fullName, useAction, type Check, type Person } from "./shared";

export type Commitment = {
  id: string;
  guarantor_applicant_id: string;
  guarantor_name: string;
  guarantor_email: string | null;
  engagement_type: "solidaire" | "simple";
  amount_cents: number;
  duration_months: number | null;
  status: string;
  act_document_id: string | null;
  invitation_sent_at: string | null;
  invitation_expires_at: string | null;
  confirmed_at: string | null;
  confirmed_name: string | null;
  confirmed_via: string | null;
  signed_on: string | null;
  signature_note: string;
};
export type Visa = {
  id: string;
  applicant_id: string | null;
  applicant_name: string | null;
  visa_number: string;
  valid_until: string;
  max_rent_cents: number;
  status: string;
  declared_via: string;
  verification_result: string | null;
  verified_on: string | null;
  verification_note: string;
  contract_signed_on: string | null;
  contract_reference: string | null;
  activated_on: string | null;
  proof_document_id: string | null;
};
type Criterion = { code: string; label: string; result: "satisfait" | "non_satisfait" | "indetermine"; explanation: string };
type Profile = {
  id: string;
  insurer_name: string;
  product_name: string;
  max_effort_rate_bp: number | null;
  accepted_situations: string[];
  min_seniority_months: number | null;
  trial_period_accepted: boolean;
  max_rent_cents: number | null;
  rules_source: string;
  rules_confirmed: boolean;
  active: boolean;
};
type Subscription = { id: string; profile_id: string | null; insurer_name: string; contract_number: string | null; status: string; effective_on: string | null; notes: string; updated_at: string };
type View = {
  application: { id: string; status: string; rent_cents: number; charges_cents: number; lease_regime: string };
  tenants: Person[];
  guarantors: { person: Person; kind: string; guarantees: string | null; documents: number; checks_by_status: Record<string, number>; checks: Check[]; commitment: Commitment | null }[];
  defaults: { duration_months: number; amount_cents: number };
  lease: { id: string; status: string } | null;
  visale: Visa[];
  visale_link: string;
  gli: {
    profiles: Profile[];
    evaluations: { profile_id: string; insurer_name: string; product_name: string; rules_confirmed: boolean; result: Criterion["result"]; criteria: Criterion[]; warning: string | null }[];
    subscriptions: Subscription[];
    situations: { value: string; label: string }[];
  };
};

export const COMMITMENT_STEPS = ["acte_prepare", "invitation_envoyee", "engagement_recueilli", "signe"];
export const COMMITMENT_LABELS: Record<string, string> = {
  acte_prepare: "Acte préparé",
  invitation_envoyee: "Garant invité",
  engagement_recueilli: "Engagement confirmé par le garant",
  signe: "Acte signé",
  annule: "Annulé",
};
export const VISALE_STEPS = ["declare", "verifie", "contrat_signe", "actif"];
export const VISALE_LABELS: Record<string, string> = { declare: "Visa déclaré", verifie: "Visa vérifié", contrat_signe: "Contrat de cautionnement signé", actif: "Garantie active", annule: "Annulé" };
const RESULT_CHIP: Record<string, { status: CheckStatus; label: string }> = {
  satisfait: { status: "conforme", label: "Satisfait" },
  non_satisfait: { status: "a_confirmer", label: "Non satisfait" },
  indetermine: { status: "non_concluant", label: "Indéterminé" },
};
const SUBSCRIPTION_LABELS: Record<string, string> = {
  en_preparation: "En préparation",
  demande_envoyee: "Demande envoyée à l’assureur",
  acceptee: "Acceptée par l’assureur, contrat à conclure",
  refusee: "Refusée par l’assureur",
  souscrite: "Contrat souscrit",
  resiliee: "Résiliée",
};

export function steps(all: string[], labels: Record<string, string>, current: string) {
  const i = all.indexOf(current);
  return all.map((s, j) => ({ title: labels[s], state: (j < i || (j === i && j === all.length - 1) ? "fait" : j === i ? "actuel" : "a-venir") as "fait" | "actuel" | "a-venir" }));
}

export function GuaranteesTab({ orgId, applicationId }: { orgId: string; applicationId: string }) {
  const org = useOrg();
  const base = `/api/v1/orgs/${orgId}/applications/${applicationId}`;
  const q = useGet<View>(`${base}/guarantees`);
  const prefixes = [base, `/api/v1/orgs/${orgId}/gli-profiles`, `/api/v1/orgs/${orgId}/decision-summary`];
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const can = org.can("operate");
  return (
    <div className="stack">
      <Card title="Garants">
        <p className="muted">
          Les pièces des garants sont examinées avec le dossier ; ici, vous suivez leurs constats et leur engagement. Un candidat ne peut jamais confirmer à la place de son garant : l’invitation est personnelle.
        </p>
        {v.guarantors.length ? (
          <div className="stack">
            {v.guarantors.map((g) => (
              <GuarantorItem key={g.person.id} g={g} v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
            ))}
          </div>
        ) : (
          <Empty title="Aucun garant déclaré dans ce dossier" />
        )}
      </Card>
      <VisaleCard v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
      <GliCard v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
    </div>
  );
}

type CardProps = { v: View; orgId: string; base: string; prefixes: string[]; can: boolean };

function GuarantorItem({ g, v, orgId, base, prefixes, can }: { g: View["guarantors"][number] } & CardProps) {
  const org = useOrg();
  const c = g.commitment;
  const [form, setForm] = useState({ engagement_type: "solidaire", amount_cents: v.defaults.amount_cents as number | null, duration_months: String(v.defaults.duration_months), indefinite: false });
  const [signedOn, setSignedOn] = useState(today());
  const [note, setNote] = useState("");
  const action = useAction(prefixes);
  const counts = Object.entries(g.checks_by_status);
  const organism = g.kind !== "personne";
  if (organism) {
    return (
      <article className="decision-echange">
        <header className="row-between">
          <strong>{g.kind === "visale" ? "Visale" : fullName(g.person) || "Organisme garant"}</strong>
          <Badge>{g.kind === "visale" ? "Garantie Visale" : "Garantie d’un organisme"}</Badge>
        </header>
        <p className="small muted">
          {g.guarantees ? `Pour ${g.guarantees}. ` : ""}
          Garantie apportée par un organisme : l’acte de cautionnement d’une personne ne s’applique pas.{g.kind === "visale" ? " Suivez les étapes du visa dans la section Visale ci-dessous." : " Conservez l’attestation de l’organisme dans le dossier."}
        </p>
      </article>
    );
  }
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>{fullName(g.person)}</strong>
          <span className="small muted">{g.guarantees ? ` · se porte caution pour ${g.guarantees}` : ""} · {g.documents} pièce(s)</span>
        </div>
        {c ? <StatusChip status={c.status === "signe" ? "conforme" : "a_confirmer"} label={COMMITMENT_LABELS[c.status]} /> : <Badge>Engagement non préparé</Badge>}
      </header>
      {counts.length ? (
        <p className="row small">
          {counts.map(([s, n]) => (
            <span key={s} className="row">
              <StatusChip status={s as CheckStatus} /> <span className="mono">{n}</span>
            </span>
          ))}
        </p>
      ) : (
        <p className="small muted">Aucun constat sur les pièces du garant pour l’instant.</p>
      )}
      {g.checks.length ? (
        <details>
          <summary>Voir les constats du garant</summary>
          <div className="stack">
            {g.checks.map((ch) => (
              <CheckLine key={ch.id} check={ch} />
            ))}
          </div>
        </details>
      ) : null}
      {c ? (
        <div className="stack-sm">
          <Fil steps={steps(COMMITMENT_STEPS, COMMITMENT_LABELS, c.status)} />
          <p className="small">
            Engagement {c.engagement_type === "solidaire" ? "solidaire" : "simple"} dans la limite de <strong>{money(c.amount_cents)}</strong>, {c.duration_months ? `pour ${c.duration_months} mois` : "pour une durée indéterminée"}.
            {c.act_document_id ? (
              <>
                {" "}
                <a href={docUrl(c.act_document_id)} target="_blank" rel="noreferrer">
                  Ouvrir l’acte de cautionnement (PDF)
                </a>
              </>
            ) : null}
          </p>
          {c.confirmed_at ? (
            <p className="small muted">
              Confirmé par {c.confirmed_name} le {dateTime(c.confirmed_at)} ({c.confirmed_via === "compte" ? "depuis son compte" : "par son lien personnel"}).
            </p>
          ) : null}
          {c.invitation_sent_at && !c.confirmed_at ? <p className="small muted">Invitation envoyée le {dateTime(c.invitation_sent_at)}, valable jusqu’au {dateTime(c.invitation_expires_at)}.</p> : null}
          {c.signed_on ? <p className="small">Acte signé le {date(c.signed_on)}{c.signature_note ? ` (${c.signature_note})` : ""}.</p> : null}
          {can ? (
            <div className="form-actions">
              {c.status === "acte_prepare" || c.status === "invitation_envoyee" ? (
                <Button small variant="secondaire" busy={action.busy === "inviter"} onClick={() => action.run("inviter", () => post(`/api/v1/orgs/${orgId}/guarantor-commitments/${c.id}/invite`, {}), "Invitation envoyée au garant (envoi simulé).")}>
                  {c.status === "invitation_envoyee" ? "Renvoyer l’invitation" : "Inviter le garant à confirmer"}
                </Button>
              ) : null}
              {c.status !== "signe" ? (
                <Button small variant="discret" busy={action.busy === "annuler"} onClick={() => action.run("annuler", () => post(`/api/v1/orgs/${orgId}/guarantor-commitments/${c.id}/cancel`, {}), "Engagement annulé.")}>
                  Annuler et préparer un autre acte
                </Button>
              ) : null}
            </div>
          ) : null}
          {can && c.status === "engagement_recueilli" ? (
            <div className="stack-sm decision-sous-formulaire">
              {v.lease ? (
                <p className="small">
                  La signature électronique se lance depuis le bail. <ButtonLink to={org.to(`baux/${v.lease.id}`)} variant="discret" small>Ouvrir le bail</ButtonLink>
                </p>
              ) : (
                <p className="small muted">La signature électronique de l’acte se lance depuis le bail (module Entrée), une fois celui-ci préparé.</p>
              )}
              <div className="form-row">
                <Input label="Signé le" type="date" value={signedOn} error={fieldError(action.error, "signed_on")} onChange={(e) => setSignedOn(e.target.value)} />
                <Input label="Précision (facultative)" placeholder="Ex. signé avec le bail" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button small busy={action.busy === "signer"} onClick={() => action.run("signer", () => post(`/api/v1/orgs/${orgId}/guarantor-commitments/${c.id}/signed`, { signed_on: signedOn, note }), "Signature de l’acte enregistrée.")}>
                  Enregistrer la signature
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : can ? (
        <form
          className="stack-sm decision-sous-formulaire"
          onSubmit={(e) => {
            e.preventDefault();
            action.run(
              "preparer",
              () =>
                post(`${base}/guarantor-commitments`, {
                  guarantor_applicant_id: g.person.id,
                  engagement_type: form.engagement_type,
                  amount_cents: form.amount_cents,
                  duration_months: form.indefinite ? null : parseInt(form.duration_months, 10) || null,
                  indefinite: form.indefinite,
                }),
              "Acte de cautionnement préparé.",
            );
          }}
        >
          <h3>Préparer l’acte de cautionnement</h3>
          <div className="form-row">
            <Select
              label="Nature de l’engagement"
              value={form.engagement_type}
              onChange={(e) => setForm({ ...form, engagement_type: e.target.value })}
              options={[
                { value: "solidaire", label: "Caution solidaire" },
                { value: "simple", label: "Caution simple" },
              ]}
            />
            <MoneyInput label="Montant maximal de l’engagement" help="Proposé : loyer et charges × durée." cents={form.amount_cents} error={fieldError(action.error, "amount_cents")} onChange={(c) => setForm({ ...form, amount_cents: c })} />
            {!form.indefinite ? (
              <Input label="Durée (mois)" inputMode="numeric" value={form.duration_months} error={fieldError(action.error, "duration_months")} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} />
            ) : null}
          </div>
          <Checkbox label="Durée indéterminée" help="L’acte reproduit alors la faculté de résiliation prévue à l’article 22-1 de la loi du 6 juillet 1989." checked={form.indefinite} onChange={(c) => setForm({ ...form, indefinite: c })} />
          <p className="small muted">L’acte reprend le loyer, les charges, la révision, la mention de la caution (article 2297 du Code civil) et l’article 22-1. Modèle proposé : faites-le relire avant signature.</p>
          <div className="form-actions">
            <Button type="submit" variant="secondaire" busy={action.busy === "preparer"}>
              Préparer l’acte
            </Button>
          </div>
          <ErrorBox error={action.error && !fieldError(action.error, "amount_cents") && !fieldError(action.error, "duration_months") ? action.error : null} />
        </form>
      ) : null}
    </article>
  );
}

function VisaleCard({ v, orgId, base, prefixes, can }: CardProps) {
  const [form, setForm] = useState({ applicant_id: v.tenants[0]?.id ?? "", visa_number: "", valid_until: "", max_rent_cents: null as number | null });
  const action = useAction(prefixes);
  const visas = v.visale.filter((x) => x.status !== "annule");
  return (
    <Card title="Visale">
      <Notice tone="attention" title="Un visa Visale n’est pas une garantie activée.">
        Vérifiez le visa dans votre espace bailleur sur <OfficialLink href={v.visale_link}>visale.fr</OfficialLink>, puis obtenez le contrat de cautionnement Visale avant de signer le bail. Loyer charges comprises de ce logement :{" "}
        {money(v.application.rent_cents + v.application.charges_cents)}.
      </Notice>
      {visas.length ? (
        <div className="stack">
          {visas.map((x) => (
            <VisaItem key={x.id} x={x} orgId={orgId} prefixes={prefixes} can={can} />
          ))}
        </div>
      ) : (
        <p className="muted">Aucun visa déclaré.</p>
      )}
      {can && v.tenants.length ? (
        <form
          className="stack-sm decision-sous-formulaire"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run("declarer", () => post(`${base}/visale`, form), "Visa enregistré : vérifiez-le sur visale.fr.");
            if (ok) setForm({ ...form, visa_number: "", valid_until: "" });
          }}
        >
          <h3>Enregistrer un visa transmis par le candidat</h3>
          <div className="form-row">
            <Select label="Locataire couvert" value={form.applicant_id} onChange={(e) => setForm({ ...form, applicant_id: e.target.value })} options={v.tenants.map((t) => ({ value: t.id, label: fullName(t) }))} />
            <Input label="Numéro de visa" required value={form.visa_number} error={fieldError(action.error, "visa_number")} onChange={(e) => setForm({ ...form, visa_number: e.target.value })} />
            <Input label="Valable jusqu’au" type="date" required value={form.valid_until} error={fieldError(action.error, "valid_until")} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            <MoneyInput label="Loyer maximal du visa" required cents={form.max_rent_cents} error={fieldError(action.error, "max_rent_cents")} onChange={(c) => setForm({ ...form, max_rent_cents: c })} />
          </div>
          <div className="form-actions">
            <Button type="submit" variant="secondaire" busy={action.busy === "declarer"}>
              Enregistrer le visa
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function VisaItem({ x, orgId, prefixes, can }: { x: Visa; orgId: string; prefixes: string[]; can: boolean }) {
  const url = `/api/v1/orgs/${orgId}/visale/${x.id}`;
  const [verify, setVerify] = useState({ result: "concordant", verified_on: today(), note: "" });
  const [contract, setContract] = useState({ signed_on: today(), reference: "" });
  const [activated, setActivated] = useState(today());
  const action = useAction(prefixes);
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>Visa {x.visa_number}</strong>
          <span className="small muted">
            {" "}
            · {x.applicant_name ?? "Locataire"} · valable jusqu’au {date(x.valid_until)} · loyer maximal {money(x.max_rent_cents)} · déclaré par le {x.declared_via}
          </span>
        </div>
        <StatusChip status={x.status === "actif" ? "conforme" : "a_confirmer"} label={VISALE_LABELS[x.status]} />
      </header>
      <Fil steps={steps(VISALE_STEPS, VISALE_LABELS, x.status)} />
      {x.verified_on ? (
        <p className="small">
          Vérification du {date(x.verified_on)} : {x.verification_result === "concordant" ? "informations concordantes" : "informations différentes de visale.fr"}
          {x.verification_note ? ` (${x.verification_note})` : ""}.
        </p>
      ) : null}
      {x.contract_signed_on ? <p className="small">Contrat de cautionnement Visale du {date(x.contract_signed_on)}{x.contract_reference ? `, référence ${x.contract_reference}` : ""}.</p> : null}
      {x.activated_on ? <p className="small">Garantie active depuis le {date(x.activated_on)}.</p> : null}
      {x.proof_document_id ? (
        <p className="small">
          <a href={docUrl(x.proof_document_id)} target="_blank" rel="noreferrer">
            Ouvrir la preuve conservée
          </a>
        </p>
      ) : null}
      {can && x.status === "declare" ? (
        <div className="form-row decision-sous-formulaire">
          <Select
            label="Résultat sur visale.fr"
            value={verify.result}
            onChange={(e) => setVerify({ ...verify, result: e.target.value })}
            options={[
              { value: "concordant", label: "Numéro, personne, validité et loyer concordent" },
              { value: "divergent", label: "Informations différentes" },
            ]}
          />
          <Input label="Vérifié le" type="date" value={verify.verified_on} onChange={(e) => setVerify({ ...verify, verified_on: e.target.value })} />
          <Input label="Note (facultative)" value={verify.note} onChange={(e) => setVerify({ ...verify, note: e.target.value })} />
          <Button small busy={action.busy === "verifier"} onClick={() => action.run("verifier", () => post(`${url}/verify`, verify), "Vérification enregistrée.")}>
            Enregistrer la vérification
          </Button>
        </div>
      ) : null}
      {can && x.status === "verifie" ? (
        <div className="form-row decision-sous-formulaire">
          <Input label="Contrat de cautionnement obtenu le" type="date" value={contract.signed_on} error={fieldError(action.error, "signed_on")} onChange={(e) => setContract({ ...contract, signed_on: e.target.value })} />
          <Input label="Référence (facultative)" value={contract.reference} onChange={(e) => setContract({ ...contract, reference: e.target.value })} />
          <Button small busy={action.busy === "contrat"} onClick={() => action.run("contrat", () => post(`${url}/contract`, { signed_on: contract.signed_on, reference: contract.reference || null }), "Contrat de cautionnement enregistré.")}>
            Enregistrer le contrat
          </Button>
        </div>
      ) : null}
      {can && x.status === "contrat_signe" ? (
        <div className="form-row decision-sous-formulaire">
          <Input label="Garantie active depuis le" type="date" value={activated} error={fieldError(action.error, "activated_on")} onChange={(e) => setActivated(e.target.value)} />
          <Button small busy={action.busy === "activer"} onClick={() => action.run("activer", () => post(`${url}/activate`, { activated_on: activated }), "Garantie Visale active.")}>
            Marquer la garantie active
          </Button>
        </div>
      ) : null}
      {can ? (
        <div className="stack-sm">
          <FileDrop
            label="Conserver une preuve (contrat Visale, vérification)"
            busy={action.busy === "preuve"}
            onFiles={(files) => {
              const fd = new FormData();
              fd.append("file", files[0]);
              action.run("preuve", () => upload(`${url}/proof`, fd), "Preuve conservée dans le dossier.");
            }}
          />
          {x.status !== "actif" ? (
            <div className="form-actions">
              <Button variant="discret" small busy={action.busy === "annuler"} onClick={() => action.run("annuler", () => post(`${url}/cancel`, {}), "Visa annulé.")}>
                Annuler ce visa
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function parsePercentToBp(s: string): number | null {
  const c = s.replace(/[\s%]/g, "").replace(",", ".");
  if (!c) return null;
  const n = Number(c);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function GliCard({ v, orgId, base, prefixes, can }: CardProps) {
  const [open, setOpen] = useState(false);
  const action = useAction(prefixes);
  return (
    <Card title="Garanties loyers impayés">
      <Notice>
        Évaluation indicative, critère par critère, à partir des conditions que vous avez saisies et des données déclarées du dossier. Seule la réponse de l’assureur engage une couverture. Ce suivi est distinct d’une simple mise en relation avec un assureur.
      </Notice>
      {v.gli.evaluations.length ? (
        <div className="stack">
          {v.gli.evaluations.map((e) => (
            <article key={e.profile_id} className="decision-echange">
              <header className="row-between">
                <strong>
                  {e.insurer_name}
                  {e.product_name ? ` — ${e.product_name}` : ""}
                </strong>
                <StatusChip status={RESULT_CHIP[e.result].status} label={`Conditions : ${RESULT_CHIP[e.result].label.toLowerCase()}`} />
              </header>
              <p className="small muted">{e.rules_confirmed ? "Conditions confirmées par l’assureur d’après vous." : "Conditions saisies par vous, non confirmées par l’assureur."}</p>
              <ul className="list-plain stack-sm">
                {e.criteria.map((c) => (
                  <li key={c.code}>
                    <StatusChip status={RESULT_CHIP[c.result].status} label={RESULT_CHIP[c.result].label} /> <strong>{c.label}</strong>
                    <span className="block small muted">{c.explanation}</span>
                  </li>
                ))}
              </ul>
              {e.warning ? <Notice tone="attention">{e.warning}</Notice> : null}
              {can ? (
                <div className="form-actions">
                  <Button small variant="secondaire" busy={action.busy === `eval-${e.profile_id}`} onClick={() => action.run(`eval-${e.profile_id}`, () => post(`${base}/gli-evaluations`, { profile_id: e.profile_id }), "Évaluation enregistrée comme constat.")}>
                    Enregistrer comme constat
                  </Button>
                  <Button small variant="discret" busy={action.busy === `sub-${e.profile_id}`} onClick={() => action.run(`sub-${e.profile_id}`, () => post(`${base}/gli-subscriptions`, { profile_id: e.profile_id }), "Suivi de souscription créé.")}>
                    Suivre une souscription
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <p className="muted">Aucun profil d’assureur actif dans cet espace.</p>
      )}

      {v.gli.subscriptions.length ? (
        <div className="stack">
          <h3>Suivi de souscription</h3>
          {v.gli.subscriptions.map((s) => (
            <SubscriptionItem key={s.id} s={s} orgId={orgId} prefixes={prefixes} can={can} />
          ))}
        </div>
      ) : null}

      <div className="stack-sm">
        <h3>Profils d’éligibilité des assureurs</h3>
        {v.gli.profiles.length ? (
          <ul className="list-plain stack-sm">
            {v.gli.profiles.map((p) => (
              <li key={p.id} className="row-between">
                <span>
                  <strong>{p.insurer_name}</strong>
                  {p.product_name ? ` — ${p.product_name}` : ""}
                  <span className="block small muted">
                    {[
                      p.max_effort_rate_bp ? `taux d’effort ≤ ${(p.max_effort_rate_bp / 100).toLocaleString("fr-FR")} %` : null,
                      p.accepted_situations.length ? `situations : ${p.accepted_situations.map((s) => v.gli.situations.find((x) => x.value === s)?.label ?? s).join(", ")}` : null,
                      p.min_seniority_months !== null ? `ancienneté ≥ ${p.min_seniority_months} mois` : null,
                      p.trial_period_accepted ? null : "pas de période d’essai en cours",
                      p.max_rent_cents ? `loyer ≤ ${money(p.max_rent_cents)}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    {p.rules_source ? ` · source : ${p.rules_source}` : ""}
                  </span>
                </span>
                <span className="row">
                  <Badge tone={p.rules_confirmed ? "succes" : "attention"}>{p.rules_confirmed ? "Confirmé" : "Hypothèse"}</Badge>
                  {!p.active ? <Badge>Inactif</Badge> : null}
                  {can ? (
                    <Button small variant="discret" busy={action.busy === `act-${p.id}`} onClick={() => action.run(`act-${p.id}`, () => patch(`/api/v1/orgs/${orgId}/gli-profiles/${p.id}`, { active: !p.active }), p.active ? "Profil désactivé." : "Profil activé.")}>
                      {p.active ? "Désactiver" : "Activer"}
                    </Button>
                  ) : null}
                  {can && !p.rules_confirmed ? (
                    <Button small variant="discret" busy={action.busy === `conf-${p.id}`} onClick={() => action.run(`conf-${p.id}`, () => patch(`/api/v1/orgs/${orgId}/gli-profiles/${p.id}`, { rules_confirmed: true }), "Conditions marquées confirmées par l’assureur.")}>
                      Conditions confirmées
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {can ? (
          <div className="form-actions">
            <Button variant="secondaire" small onClick={() => setOpen(true)}>
              Ajouter un profil d’assureur
            </Button>
          </div>
        ) : null}
      </div>
      <ProfileModal open={open} onClose={() => setOpen(false)} orgId={orgId} prefixes={prefixes} situations={v.gli.situations} />
    </Card>
  );
}

function SubscriptionItem({ s, orgId, prefixes, can }: { s: Subscription; orgId: string; prefixes: string[]; can: boolean }) {
  const [form, setForm] = useState({ status: s.status, contract_number: s.contract_number ?? "", effective_on: s.effective_on ?? "" });
  const action = useAction(prefixes);
  return (
    <article className="decision-echange">
      <header className="row-between">
        <strong>{s.insurer_name}</strong>
        <StatusChip status={s.status === "souscrite" ? "conforme" : s.status === "refusee" || s.status === "resiliee" ? "info" : "a_confirmer"} label={SUBSCRIPTION_LABELS[s.status] ?? s.status} />
      </header>
      {s.status !== "souscrite" ? <p className="small muted">Aucune couverture tant que l’assureur n’a pas confirmé un contrat.</p> : <p className="small">Contrat {s.contract_number}, effet le {date(s.effective_on)}.</p>}
      {can ? (
        <div className="form-row">
          <Select label="État" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={Object.entries(SUBSCRIPTION_LABELS).map(([value, label]) => ({ value, label }))} />
          <Input label="Numéro de contrat" value={form.contract_number} error={fieldError(action.error, "contract_number")} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} />
          <Input label="Date d’effet" type="date" value={form.effective_on} error={fieldError(action.error, "effective_on")} onChange={(e) => setForm({ ...form, effective_on: e.target.value })} />
          <Button small busy={action.busy === "maj"} onClick={() => action.run("maj", () => patch(`/api/v1/orgs/${orgId}/gli-subscriptions/${s.id}`, { status: form.status, contract_number: form.contract_number || null, effective_on: form.effective_on || null }), "Suivi mis à jour.")}>
            Mettre à jour
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function ProfileModal({ open, onClose, orgId, prefixes, situations }: { open: boolean; onClose: () => void; orgId: string; prefixes: string[]; situations: { value: string; label: string }[] }) {
  const empty = { insurer_name: "", product_name: "", effort: "33", accepted_situations: ["cdi", "fonctionnaire"] as string[], min_seniority_months: "", trial_period_accepted: false, max_rent_cents: null as number | null, rules_source: "", rules_confirmed: false };
  const [form, setForm] = useState(empty);
  const action = useAction(prefixes);
  const submit = async () => {
    const ok = await action.run(
      "profil",
      () =>
        post(`/api/v1/orgs/${orgId}/gli-profiles`, {
          insurer_name: form.insurer_name,
          product_name: form.product_name,
          max_effort_rate_bp: parsePercentToBp(form.effort),
          accepted_situations: form.accepted_situations,
          min_seniority_months: form.min_seniority_months ? parseInt(form.min_seniority_months, 10) : null,
          trial_period_accepted: form.trial_period_accepted,
          max_rent_cents: form.max_rent_cents,
          rules_source: form.rules_source,
          rules_confirmed: form.rules_confirmed,
        }),
      "Profil d’assureur ajouté.",
    );
    if (ok) {
      setForm(empty);
      onClose();
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Profil d’éligibilité d’un assureur"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button busy={action.busy === "profil"} onClick={submit}>
            Enregistrer le profil
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted">Saisissez les conditions communiquées par l’assureur. Elles restent des hypothèses tant que vous ne les avez pas confirmées avec lui.</p>
        <div className="form-row">
          <Input label="Assureur" required value={form.insurer_name} error={fieldError(action.error, "insurer_name")} onChange={(e) => setForm({ ...form, insurer_name: e.target.value })} />
          <Input label="Produit (facultatif)" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} />
          <Input label="Taux d’effort maximal (%)" inputMode="decimal" value={form.effort} error={fieldError(action.error, "max_effort_rate_bp")} onChange={(e) => setForm({ ...form, effort: e.target.value })} />
          <Input label="Ancienneté minimale (mois)" inputMode="numeric" value={form.min_seniority_months} onChange={(e) => setForm({ ...form, min_seniority_months: e.target.value })} />
          <MoneyInput label="Loyer charges comprises maximal (facultatif)" cents={form.max_rent_cents} onChange={(c) => setForm({ ...form, max_rent_cents: c })} />
        </div>
        <fieldset className="decision-choix">
          <legend>Situations acceptées</legend>
          <div className="decision-cases">
            {situations.map((s) => (
              <Checkbox
                key={s.value}
                label={s.label}
                checked={form.accepted_situations.includes(s.value)}
                onChange={(c) => setForm({ ...form, accepted_situations: c ? [...form.accepted_situations, s.value] : form.accepted_situations.filter((x) => x !== s.value) })}
              />
            ))}
          </div>
        </fieldset>
        <Checkbox label="Période d’essai en cours acceptée" checked={form.trial_period_accepted} onChange={(c) => setForm({ ...form, trial_period_accepted: c })} />
        <Input label="Source des conditions" placeholder="Ex. conditions générales transmises le 12 septembre 2026" value={form.rules_source} onChange={(e) => setForm({ ...form, rules_source: e.target.value })} />
        <Checkbox label="Conditions confirmées par l’assureur" checked={form.rules_confirmed} onChange={(c) => setForm({ ...form, rules_confirmed: c })} />
        <ErrorBox error={action.error && !fieldError(action.error, "insurer_name") ? action.error : null} />
      </div>
    </Modal>
  );
}
